'use client'

import { useState, useRef, forwardRef } from 'react'

import {
  DndContext,
  useDraggable,
  useSensor,
  useSensors,
  PointerSensor,
  TouchSensor,
} from '@dnd-kit/core'

type DraggableAreaProps = {
  children: React.ReactNode
  className?: string
}

// Create a draggable component for horizontal scrolling
const DraggableArea = forwardRef<HTMLDivElement, DraggableAreaProps>(
  ({ children, className }, ref) => {
    const containerRef = useRef<HTMLDivElement>(null)
    const [scrollStartPos, setScrollStartPos] = useState(0)

    const sensors = useSensors(
      useSensor(PointerSensor, {
        activationConstraint: { distance: 5 },
      }),
      useSensor(TouchSensor, {
        activationConstraint: { delay: 50, tolerance: 5 },
      }),
    )

    const handleDragStart = () => {
      if (containerRef.current) {
        setScrollStartPos(containerRef.current.scrollLeft)
      }
    }

    const handleDragMove = (event) => {
      if (containerRef.current) {
        containerRef.current.scrollLeft = scrollStartPos - event.delta.x
      }
    }

    // Assign both the forwarded ref and the local ref
    const setRefs = (node: HTMLDivElement) => {
      containerRef.current = node
      if (typeof ref === 'function') {
        ref(node)
      } else if (ref) {
        ;(ref as React.MutableRefObject<HTMLDivElement | null>).current = node
      }
    }

    return (
      <DndContext sensors={sensors} onDragStart={handleDragStart} onDragMove={handleDragMove}>
        <DraggableContent>
          <div
            ref={setRefs}
            className={className}
            style={{
              scrollbarWidth: 'none',
              scrollBehavior: 'smooth', // Makes scrolling smoother
              scrollSnapType: 'x mandatory', // Enables horizontal snap points
            }}>
            {children}

            <style jsx>{`
              div::-webkit-scrollbar {
                display: none;
              }
            `}</style>
          </div>
        </DraggableContent>
      </DndContext>
    )
  },
)

DraggableArea.displayName = 'DraggableArea'

// Draggable content wrapper
function DraggableContent({ children }) {
  const { attributes, listeners, setNodeRef } = useDraggable({
    id: 'scrollable-content',
  })

  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      className="cursor-grab active:cursor-grabbing">
      {children}
    </div>
  )
}

export default DraggableArea
